// Call Record Type Definition
export type UrgencyLevel = 'low' | 'medium' | 'high' | 'extra_high' | 'no';

export interface CallRecord {
  id: string;
  created_at: string;
  name: string | null;
  call_duration_sec: number | null;
  phone_number: string | null;
  call_summary: string | null;
  category: string | null;
  service_type: string | null;
  inquiry: string | null;
  status: boolean | null;
  conversation_id: string | null;
  audio_uri: string | null;
  notes: string | null;
  urgency: UrgencyLevel;
}

// Database Insert Type (without id and created_at)
export type CallRecordInsert = Omit<CallRecord, 'id' | 'created_at'>;

// Database Update Type
export type CallRecordUpdate = Partial<CallRecordInsert>;

// User Profile Type
export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'manager' | 'user';
  created_at: string;
  updated_at: string;
}

// Filter Types
export interface DateFilter {
  preset: 'all' | 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom' | null;
  from: string | null;
  to: string | null;
}

export interface FilterState {
  search: string;
  dateFilter: DateFilter;
  category: string | null;
  serviceType: string | null;
  status: boolean | null;
  urgency: UrgencyLevel | null;
}

// Pagination Types
export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

// Sort Types
export interface SortState {
  column: keyof CallRecord | null;
  direction: 'asc' | 'desc';
}

// Analytics Types
export interface AnalyticsMetrics {
  // Basic metrics
  totalCalls: number;
  resolvedCalls: number;
  pendingCalls: number;
  avgDuration: number;
  resolvedPercentage: number;
  pendingPercentage: number;
  
  // Urgency distribution
  byUrgency: Record<UrgencyLevel, number>;
  highUrgencyCount: number;
  highUrgencyRate: number;
  
  // At-risk calls (high urgency, pending)
  atRiskCalls: CallRecord[];
  atRiskCount: number;
  
  // Cross-tabulation: urgency x status
  byUrgencyAndStatus: Record<string, { resolved: number; pending: number }>;
  
  // Service type efficiency
  serviceTypeEfficiency: Record<string, {
    total: number;
    resolved: number;
    pending: number;
    avgDuration: number;
    resolutionRate: number;
  }>;
  
  // Time-based analytics
  peakHours: { hour: number; count: number }[];
  avgHandleTimeByCategory: Record<string, number>;
  avgHandleTimeByServiceType: Record<string, number>;
}

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string;
    fill?: boolean;
    tension?: number;
  }[];
}

// Supabase Realtime Payload
export type RealtimePayload<T> = {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: T;
  old: T;
};

// Export Types
export interface ExportOptions {
  format: 'csv' | 'pdf';
  columns: (keyof CallRecord)[];
  includeHeaders: boolean;
  dateRange?: DateFilter;
}

// API Response Types
export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

// Toast/Notification Types
export interface Toast {
  id: string;
  title: string;
  description?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

// Language Types
export type Language = 'en' | 'ar';

export interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// Theme Types
export type Theme = 'light' | 'dark' | 'system';

export interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
}

// Table Configuration
export interface TableConfig {
  hiddenColumns: (keyof CallRecord)[];
  columnOrder: (keyof CallRecord)[];
  pageSize: number;
}

// Dashboard Stats
export interface DashboardStats {
  todayCalls: number;
  weekCalls: number;
  monthCalls: number;
  avgResponseTime: number;
  resolutionRate: number;
}

// Urgency Configuration for UI
export const URGENCY_CONFIG: Record<UrgencyLevel, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: '#15803d', bg: '#dcfce7' },
  medium: { label: 'Medium', color: '#b45309', bg: '#fef3c7' },
  high: { label: 'High', color: '#b91c1c', bg: '#fee2e2' },
  extra_high: { label: 'Extra High', color: '#7c3aed', bg: '#ede9fe' },
  no: { label: 'No', color: '#6b7280', bg: '#f3f4f6' },
};
